import React, { useState } from "react";
import { useAppointment } from "components/appointments/api";
import { useParams } from "helpers/tixxt-router";
import PageTitle, { Subtitle } from "components/layout/PageTitle";
import { camelCase, includes, isEmpty, startsWith, upperFirst } from "lodash";
import Toolbar from "components/appointments/details/Toolbar";
import AppointmentDetails from "components/appointments/details/AppointmentDetails";
import TranslationDropDown, {
  ORIGINAL_LANGUAGE_SIGNIFIER,
} from "components/shared/TranslationDropDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRepeat } from "@fortawesome/pro-regular-svg-icons";
import PageLoading from "components/shared/PageLoading";
import CustomConfigurator from "components/../CustomConfigurator";
import TranslationIndicator from "components/shared/TranslationIndicator";
import { isBefore } from "date-fns";
import { TZDate } from "@date-fns/tz";

export default function AppointmentPage() {
  const { id } = useParams();
  const { data: appointment, isLoading } = useAppointment(id);

  // appointment types with custom forms: bitkom_campaign
  const AppointmentPageComponent = includes(
    ["appointment", "recurring_appointment", "virtual_appointment"],
    appointment?.type,
  )
    ? DefaultAppointmentPage
    : CustomConfigurator.get(
        `appointments.show.${upperFirst(camelCase(appointment?.type))}`,
      );

  if (isLoading) {
    return <PageLoading />;
  }

  return <>{AppointmentPageComponent && <AppointmentPageComponent />}</>;
}

export function DefaultAppointmentPage() {
  const { id } = useParams();
  const [appointmentLang, setAppointmentLang] = useState<string | undefined>();
  const {
    data: appointment,
    isLoading,
    refetch,
  } = useAppointment(id, appointmentLang);

  const isRecurring =
    appointment?.type === "recurring_appointment" &&
    !startsWith(appointment?.id, "r-");

  const isModifiedRecurring =
    appointment?.type === "virtual_appointment" &&
    startsWith(appointment?.id, "r-");

  const handleLanguageSelect = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    language: string,
  ) => {
    if (language === ORIGINAL_LANGUAGE_SIGNIFIER) {
      setAppointmentLang(undefined);
    } else {
      setAppointmentLang(language);
    }
  };

  const today = new Date();
  const appointmentStart = appointment?.date?.start
    ? new Date(appointment.date.start)
    : new Date();

  const appointmentEnd = appointment?.date?.end
    ? new Date(appointment.date.end)
    : null;

  const isPast =
    isBefore(appointmentStart, today) &&
    (isEmpty(appointmentEnd) ||
      (appointmentEnd && isBefore(appointmentEnd, today))) &&
    ((!isRecurring ||
      (appointment.recurrence_pattern &&
        appointment.recurrence_pattern.recurrence_end_time &&
        isBefore(
          new TZDate(
            appointment.recurrence_pattern.recurrence_end_time,
            appointment.date.time_zone,
          ),
          new TZDate(appointment.date.time_zone),
        ))) as boolean);

  return isLoading ? (
    <PageLoading />
  ) : (
    appointment && (
      <>
        <PageTitle title={appointment?.name}>
          {(isRecurring || isModifiedRecurring) && (
            <FontAwesomeIcon
              icon={faArrowsRepeat}
              title={I18n.t("js.recurring_appointment.icon.title")}
            />
          )}
          <TranslationDropDown handleLanguageSelect={handleLanguageSelect} />
          {appointmentLang && <TranslationIndicator />}
        </PageTitle>
        <Subtitle className="mb-3">
          {isRecurring
            ? I18n.t("js.calendars.recurring_appointment.subtitle")
            : I18n.t("js.calendars.appointment.subtitle")}
        </Subtitle>
        <Toolbar
          id={appointment.id}
          type={appointment.type}
          can={appointment.can}
          participationLocked={
            appointment.attendee_configuration.participation_locked
          }
          participantsEmpty={isEmpty(appointment.participants)}
          isRecurring={isRecurring}
          name={appointment.name}
          isModifiedRecurring={isModifiedRecurring}
          previousOccurrenceUrl={appointment.previous_occurrence_url}
          recurringAppointmentUrl={appointment.recurring_appointment_url}
          nextOccurrenceUrl={appointment.next_occurrence_url}
          refetch={refetch}
          isPast={isPast}
          canceled={appointment.canceled}
          cancelProhibitedReason={
            appointment.canceled ? "" : appointment.cancel_prohibited_reason
          }
        />
        <AppointmentDetails
          {...appointment}
          isRecurring={isRecurring}
          isPast={isPast}
          refetch={refetch}
          translated={!!appointmentLang}
        />
      </>
    )
  );
}
